import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Church,
  Gift,
  Mail,
  BookOpen,
  Flower2,
  Gem,
  Crown,
  Calendar,
  MapPin,
  Palette,
  Utensils,
  Music,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';

// Wedding item types
const WEDDING_ITEM_TYPES = [
  { value: 'bomboniere', label: 'Bomboniere', icon: Gift },
  { value: 'inviti', label: 'Inviti e partecipazioni', icon: Mail },
  { value: 'tableau', label: 'Tableau de mariage', icon: BookOpen },
  { value: 'segnaposto', label: 'Segnaposto', icon: Flower2 },
  { value: 'guestbook', label: 'Guestbook / Libro firme', icon: BookOpen },
  { value: 'decorazioni', label: 'Decorazioni e allestimenti', icon: Sparkles },
  { value: 'bouquet', label: 'Bouquet e fiori', icon: Flower2 },
  { value: 'accessori_sposa', label: 'Accessori sposa', icon: Crown },
  { value: 'accessori_sposo', label: 'Accessori sposo', icon: Gem },
  { value: 'fedi', label: 'Fedi e gioielli', icon: Gem },
  { value: 'album', label: 'Album fotografico', icon: Camera },
  { value: 'altro', label: 'Altro', icon: Heart },
];

// Wedding style options
const WEDDING_STYLES = [
  { value: 'classico', label: 'Classico / Elegante', icon: Crown },
  { value: 'romantico', label: 'Romantico', icon: Heart },
  { value: 'rustico', label: 'Rustico / Country', icon: Flower2 },
  { value: 'boho', label: 'Boho Chic', icon: Sparkles },
  { value: 'moderno', label: 'Moderno / Minimal', icon: Gem },
  { value: 'vintage', label: 'Vintage / Retrò', icon: Camera },
  { value: 'marino', label: 'Marino / Beach', icon: Sparkles },
  { value: 'shabby', label: 'Shabby Chic', icon: Flower2 },
  { value: 'altro', label: 'Altro stile', icon: Palette },
];

// Quantity options
const QUANTITY_OPTIONS = [
  { value: '1-10', label: '1-10 pezzi', description: 'Campione / prova' },
  { value: '10-30', label: '10-30 pezzi', description: 'Matrimonio intimo' },
  { value: '30-50', label: '30-50 pezzi', description: 'Cerimonia piccola' },
  { value: '50-100', label: '50-100 pezzi', description: 'Cerimonia media' },
  { value: '100-150', label: '100-150 pezzi', description: 'Cerimonia grande' },
  { value: '150-200', label: '150-200 pezzi', description: 'Grande evento' },
  { value: '200+', label: '200+ pezzi', description: 'Evento extra large' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '0-100', label: '€0 - €100', description: 'Dettagli semplici' },
  { value: '100-300', label: '€100 - €300', description: 'Bomboniere base' },
  { value: '300-500', label: '€300 - €500', description: 'Set completo base' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Set elaborato' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Allestimento parziale' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Allestimento completo' },
  { value: '5000+', label: '€5.000+', description: 'Wedding luxury' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3 Mesi', description: 'Con calma' },
  { value: '6months', label: '6 Mesi', description: 'Pianificazione' },
  { value: '1year', label: '1 Anno', description: 'Largo anticipo' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example wedding items
const WEDDING_EXAMPLES = [
  {
    idea: "Bomboniere matrimonio tema mare",
    result: "Sacchettini in lino con conchiglie in ceramica dipinte a mano",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop",
    category: "Bomboniere"
  },
  {
    idea: "Inviti matrimonio stile botanico",
    result: "Partecipazioni in carta cotone con fiori pressati e calligrafia",
    image: "https://images.unsplash.com/photo-1607190074257-dd4b7af0309f?w=400&h=300&fit=crop",
    category: "Inviti"
  },
  {
    idea: "Segnaposto personalizzati rustic chic",
    result: "Dischetti in legno incisi con nome ospite e fiori secchi",
    image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop",
    category: "Segnaposto"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Le bomboniere erano perfette, ogni ospite è rimasto incantato. L'artigiana ha seguito ogni dettaglio del nostro tema.",
    author: "Giulia & Marco",
    location: "Matrimonio a Firenze"
  },
  {
    text: "Inviti fatti a mano che hanno emozionato tutti. La qualità della carta e della calligrafia era impeccabile.",
    author: "Sara & Luca",
    location: "Matrimonio sul Lago di Como"
  },
  {
    text: "Il tableau e i segnaposto hanno trasformato la location. Artigianato italiano al suo meglio!",
    author: "Chiara & Andrea",
    location: "Matrimonio in Toscana"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi cosa cerchi per il tuo matrimonio e ricevi proposte da artigiani specializzati senza alcun costo."
  },
  {
    question: "Sono obbligato ad acquistare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma insieme al tuo partner e decidi solo se trovi qualcosa che vi emoziona."
  },
  {
    question: "Con quanto anticipo devo ordinare?",
    answer: "Consigliamo almeno 2-3 mesi per bomboniere e inviti, 1 mese per segnaposto. Per allestimenti complessi, meglio 6 mesi."
  },
  {
    question: "Posso richiedere un campione?",
    answer: "Certamente! Molti artigiani offrono la possibilità di creare un campione prima dell'ordine completo."
  },
  {
    question: "Gli artigiani sono esperti di matrimoni?",
    answer: "Sì, su Rarifex trovi artigiani specializzati in wedding: calligrafi, ceramisti, fioristi, cartotecnici e molto altro."
  }
];

export default function MatrimonioRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wedding_item_type: '',
    wedding_style: '',
    quantity: '',
    budget: '',
    timeline: '',
    wedding_date: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Matrimonio Artigianale | Wedding Made in Italy | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `wedding-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi cosa cerchi per il tuo matrimonio');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_wedding_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const itemTypeLabel = WEDDING_ITEM_TYPES.find(t => t.value === formData.wedding_item_type)?.label || '';
      const styleLabel = WEDDING_STYLES.find(s => s.value === formData.wedding_style)?.label || '';
      const quantityLabel = QUANTITY_OPTIONS.find(q => q.value === formData.quantity)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (itemTypeLabel) details.push(`💍  Tipo articolo: ${itemTypeLabel}`);
      if (styleLabel) details.push(`🎨  Stile matrimonio: ${styleLabel}`);
      if (quantityLabel) details.push(`📦  Quantità: ${quantityLabel}`);
      if (formData.wedding_date) details.push(`📅  Data matrimonio: ${formData.wedding_date}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi consegna: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `Matrimonio - ${itemTypeLabel} ${styleLabel}`.trim() || 'Richiesta matrimonio',
        description: fullDescription,
        category: 'Matrimonio',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('wedding-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Matrimonio Artigianale | Bomboniere, Inviti e Decorazioni Made in Italy | Rarifex</title>
        <meta name="description" content="Rendi unico il tuo matrimonio con creazioni artigianali italiane: bomboniere, inviti, tableau, segnaposto. Descrivi il tuo stile e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="matrimonio artigianale, bomboniere matrimonio, inviti matrimonio, tableau mariage, segnaposto matrimonio, decorazioni nozze, wedding made in italy, matrimonio italiano" />
        <link rel="canonical" href="https://www.rarifex.com/matrimonio-artigianale" />
        <meta property="og:title" content="Matrimonio Artigianale | Wedding Made in Italy | Rarifex" />
        <meta property="og:description" content="Rendi unico il tuo matrimonio con creazioni artigianali italiane. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/matrimonio-artigianale" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-rose-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Heart className="w-4 h-4" />
                    Matrimonio Artigianale
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il tuo matrimonio{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-rose-400">
                      unico e indimenticabile
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani creano bomboniere, inviti e decorazioni su misura per il tuo grande giorno.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi il tuo stile e ricevi proposte personalizzate da artigiani specializzati in wedding.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani wedding specialist</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="wedding-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo della richiesta
                        </Label>
                        <Input
                          placeholder="Es: Bomboniere matrimonio tema provenzale"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="wedding-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi cosa cerchi *
                        </Label>
                        <Textarea
                          placeholder="Racconta cosa desideri per il tuo matrimonio... Stile, colori, materiali preferiti, tema della cerimonia, dettagli particolari che vorresti..."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="wedding-description-textarea"
                        />
                      </div>
                      
                      {/* Item Type & Style */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Cosa ti serve?
                          </Label>
                          <select
                            value={formData.wedding_item_type}
                            onChange={(e) => handleInputChange('wedding_item_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wedding-item-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {WEDDING_ITEM_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Stile matrimonio
                          </Label>
                          <select
                            value={formData.wedding_style}
                            onChange={(e) => handleInputChange('wedding_style', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wedding-style-select"
                          >
                            <option value="">Seleziona...</option>
                            {WEDDING_STYLES.map(style => (
                              <option key={style.value} value={style.value}>{style.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Quantity & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quantità</Label>
                          <select
                            value={formData.quantity}
                            onChange={(e) => handleInputChange('quantity', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wedding-quantity-select"
                          >
                            <option value="">Seleziona...</option>
                            {QUANTITY_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wedding-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline & Wedding Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wedding-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Data matrimonio</Label>
                          <Input
                            type="date"
                            value={formData.wedding_date}
                            onChange={(e) => handleInputChange('wedding_date', e.target.value)}
                            className="mt-1.5"
                            data-testid="wedding-date-input"
                          />
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di ispirazione (consigliate!)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-300 hover:bg-amber-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="wedding-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto Pinterest, mood board... ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-amber-500 to-rose-400 hover:from-amber-600 hover:to-rose-500 text-white shadow-lg shadow-amber-200"
                        data-testid="wedding-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Heart className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Il matrimonio perfetto merita dettagli perfetti
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-amber-400">✗</span>
                  Le bomboniere in serie sono tutte uguali
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-amber-400">✗</span>
                  Gli inviti standard non trasmettono emozione
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-amber-400">✗</span>
                  Vuoi qualcosa che rispecchi davvero voi due
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un giorno così speciale, servono mani artigiane.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Creazioni{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-rose-400">
                  uniche come il vostro amore
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Artigiani italiani specializzati in wedding pronti a realizzare la vostra visione.
                <br />
                <strong className="text-slate-800">Voi sognate, loro creano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Raccontate il vostro stile</h3>
                  <p className="text-slate-600">Descrivete tema, colori e atmosfera del vostro matrimonio</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-rose-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Artigiani vi propongono</h3>
                  <p className="text-slate-600">Ricevete proposte personalizzate da esperti di wedding</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegliete insieme</h3>
                  <p className="text-slate-600">Nessun obbligo, decidete solo se vi emoziona</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da sogno a realtà
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri artigiani per altri sposi
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {WEDDING_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-amber-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-amber-200 text-amber-600 hover:bg-amber-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivete il vostro matrimonio</h3>
                    <p className="text-slate-600">
                      Raccontate lo stile, i colori, il tema. Allegate foto di ispirazione da Pinterest o riviste. Più dettagli date, meglio è.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-rose-400 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevete proposte</h3>
                    <p className="text-slate-600">
                      Artigiani specializzati vedono la vostra richiesta e vi inviano proposte con foto, prezzi e tempi di realizzazione.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegliete con calma</h3>
                    <p className="text-slate-600">
                      Confrontate le proposte, chattate con gli artigiani, richiedete campioni. Nessun obbligo, scegliete solo se vi convince.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere l'artigianato per il matrimonio
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <CheckCircle className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Pezzi unici</h3>
                    <p className="text-sm text-slate-600">Nessun altro avrà le stesse bomboniere</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-rose-50">
                  <Sparkles className="w-8 h-8 text-rose-400 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Personalizzazione totale</h3>
                    <p className="text-sm text-slate-600">Ogni dettaglio come lo desiderate voi</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Qualità garantita</h3>
                    <p className="text-sm text-slate-600">Materiali pregiati e lavorazione impeccabile</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo, campioni disponibili</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono gli sposi
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Heart className="w-4 h-4 text-amber-500" />
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`wedding-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50 to-rose-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Heart className="w-16 h-16 text-rose-400 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronti a rendere unico il vostro giorno?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Gli artigiani italiani aspettano di creare per voi.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-amber-500 to-rose-400 hover:from-amber-600 hover:to-rose-500 text-white shadow-lg shadow-amber-200"
                data-testid="wedding-cta-final-button"
              >
                <Heart className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artigiani, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/matrimonio-artigianale')}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
